import { useState, useEffect } from 'react';
import { Wind, Shield, Cpu, ChevronRight, Check, ArrowRight } from 'lucide-react';
import { AirParticles } from '@/components/Layout';
import logger from '@/utils/logger';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Product({ onBookDemo }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data.products || [])).catch(err => logger.error('Products fetch error:', err.message));
  }, []);

  const icons = { window: Shield, purifier: Wind, sensor: Cpu };
  const colors = { window: 'var(--deep-teal)', purifier: 'var(--aqua)', sensor: '#8B5CF6' };

  const vayuPane = products.find(p => p.id === 'vayu-pane');

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden" style={{ background: 'var(--soft-white)' }} data-testid="product-hero">
        <AirParticles count={10} color="rgba(15,76,92,0.04)" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="overline-teal mb-4 animate-fade-up">Vayu Product Line</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 animate-fade-up delay-100" style={{ fontFamily: 'Inter' }} data-testid="product-page-title">
              The ecosystem<br />for <span style={{ color: 'var(--aqua)' }}>clean air</span>.
            </h1>
            <p className="text-base lg:text-lg leading-relaxed mb-10 animate-fade-up delay-200" style={{ color: 'var(--text-secondary)' }}>
              Three products working in harmony. Vayu Pane filters, Vayu Pure purifies, Vayu Sense orchestrates.
            </p>
          </div>
        </div>
      </section>

      {/* Vayu Pane Deep Dive */}
      {vayuPane && (
        <section className="section-padding px-6 lg:px-12 bg-teal-gradient relative overflow-hidden" data-testid="vayu-pane-section">
          <AirParticles count={15} color="rgba(63,167,214,0.08)" />
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--aqua)' }}>Flagship Product</div>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Inter' }}>Vayu Pane</h2>
                <p className="text-xl text-white/60 mb-6" style={{ fontFamily: 'Inter' }}>The Smart Window</p>
                <p className="text-base leading-relaxed text-white/50 mb-8">{vayuPane.description}</p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {vayuPane.specs && Object.entries(vayuPane.specs).map(([k, v]) => (
                    <div key={k} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-[10px] uppercase tracking-wider text-white/30 mb-1">{k.replace('_', ' ')}</p>
                      <p className="text-sm font-semibold text-white">{v}</p>
                    </div>
                  ))}
                </div>
                <button onClick={onBookDemo} className="btn-aqua flex items-center gap-2" data-testid="pane-demo-btn">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                  <img src="https://images.unsplash.com/photo-1758315417336-38777d626815?w=700&q=75" alt="Vayu Pane installed" className="w-full h-80 object-cover" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Products Grid */}
      <section className="section-padding px-6 lg:px-12" data-testid="all-products">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="overline-teal mb-4">Complete Ecosystem</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Inter' }}>Three products. One system.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((p, i) => {
              const Icon = icons[p.category] || Wind;
              const color = colors[p.category] || 'var(--deep-teal)';
              return (
                <div key={p.id} className="card-hover p-8 animate-fade-up" style={{ animationDelay: `${i * 0.15}s` }} data-testid={`product-detail-${i}`}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${color}10` }}>
                    <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color, fontFamily: 'Inter' }}>{p.tagline}</p>
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Inter' }}>{p.name}</h3>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>{p.description}</p>
                  <ul className="space-y-2">
                    {p.features?.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <Check className="w-4 h-4 flex-shrink-0" style={{ color }} strokeWidth={2} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
